import Vue from 'vue';
import Vuex from 'vuex';
import VueI18n from 'vue-i18n';
import qs from 'qs';
import axios from 'axios';
import moment from 'moment';
import router from '@/router';
import config from '@/config';
import { pathOr, find } from 'lodash/fp';
import de from '@/locale/de/index.json';
import fr from '@/locale/fr/index.json';
import en from '@/locale/en/index.json';
import it from '@/locale/it/index.json';
import bg from '@/locale/bg/index.json';
import getters from './getters';
import api from './api';
import {
  defaultFilterState,
  getInitialFilter,
  getInitialLanguage,
  getInitialIsFilterOpen,
} from './functions';
import Highcharts from 'highcharts';

Vue.use(Vuex);
Vue.use(VueI18n);

const A = axios.create({ withCredentials: true });

const highchartsLang = {
  en: {
    decimalPoint: '.',
    thousandsSep: '\u0020',
    loading: 'Daten werden geladen...',
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    weekdays: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    shortMonths: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    exportButtonTitle: 'Export',
    printButtonTitle: 'Print',
    rangeSelectorFrom: 'To',
    rangeSelectorTo: 'From',
    rangeSelectorZoom: 'Range',
    downloadPNG: 'Download PNG',
    downloadJPEG: 'Download JPEG',
    downloadPDF: 'Download PDF',
    downloadSVG: 'Download SVG',
    resetZoom: 'Reset zoom',
    resetZoomTitle: 'Reset zoom level 1:1',
    viewFullscreen: 'View in full screen',
    noData: 'No Data',
  },
  de: {
    decimalPoint: ',',
    thousandsSep: '.',
    loading: 'Daten werden geladen...',
    months: [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ],
    weekdays: [
      'Sonntag',
      'Montag',
      'Dienstag',
      'Mittwoch',
      'Donnerstag',
      'Freitag',
      'Samstag',
    ],
    shortMonths: [
      'Jan',
      'Feb',
      'Mär',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dez',
    ],
    exportButtonTitle: 'Exportieren',
    printButtonTitle: 'Drucken',
    rangeSelectorFrom: 'Von',
    rangeSelectorTo: 'Bis',
    rangeSelectorZoom: 'Zeitraum',
    downloadPNG: 'Download als PNG-Bild',
    downloadJPEG: 'Download als JPEG-Bild',
    downloadPDF: 'Download als PDF-Dokument',
    downloadSVG: 'Download als SVG-Bild',
    resetZoom: 'Zoom zurücksetzen',
    resetZoomTitle: 'Zoom zurücksetzen',
    viewFullscreen: 'Im Vollbildmodus anzeigen',
    noData: 'Keine Daten vorhanden',
  },
  fr: {
    months: [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ],
    weekdays: [
      'Dimanche',
      'Lundi',
      'Mardi',
      'Mercredi',
      'Jeudi',
      'Vendredi',
      'Samedi',
    ],
    shortMonths: [
      'janv',
      'fevr',
      'mars',
      'avr',
      'mai',
      'juin',
      'juil',
      'aout',
      'sept',
      'oct',
      'nov',
      'dec',
    ],
    decimalPoint: ',',
    downloadPNG: 'Télécharger en image PNG',
    downloadJPEG: 'Télécharger en image JPEG',
    downloadPDF: 'Télécharger en document PDF',
    downloadSVG: 'Télécharger en document Vectoriel',
    exportButtonTitle: 'Export du graphique',
    loading: 'Chargement en cours...',
    printButtonTitle: 'Imprimer le graphique',
    resetZoom: 'Réinitialiser le zoom',
    resetZoomTitle: 'Réinitialiser le zoom au niveau 1:1',
    thousandsSep: ' ',
    viewFullscreen: 'Montrer au mode plein écran',
    noData: 'Pas de données',
  },
  it: {
    months: [
      'Gennaio',
      'Febbraio',
      'Marzo',
      'Aprile',
      'Maggio',
      'Giugno',
      'Luglio',
      'Agosto',
      'Settembre',
      'Ottobre',
      'Novembre',
      'Dicembre',
    ],
    weekdays: [
      'Domenica',
      'Lunedì',
      'Martedì',
      'Mercoledì',
      'Giovedì',
      'Venerdì',
      'Sabato',
    ],
    shortMonths: [
      'gen',
      'feb',
      'mar',
      'apr',
      'mag',
      'giu',
      'giu',
      'lug',
      'set',
      'ott',
      'nov',
      'dic',
    ],
    decimalPoint: ',',
    downloadPNG: 'Scaricare immagini PNG',
    downloadJPEG: 'Scaricare immagini JPEG',
    downloadPDF: 'Scaricare file PDF',
    downloadSVG: 'Scaricare file Vettori scalabili',
    exportButtonTitle: 'Export de grafica',
    loading: 'Attendere, caricamento dati in corso...',
    printButtonTitle: 'Stampare la grafica',
    resetZoom: 'Reinizializzare lo zoom',
    resetZoomTitle: "Reinizializzare lo zoom a'livello 1:1",
    thousandsSep: ' ',
    viewFullscreen: 'Modalità a schermo intero',
    noData: 'Nessun dato disponibile',
  },
  bg: {
    months: [
      'Януари',
      'Февруари',
      'Март',
      'Април',
      'Май',
      'Юни',
      'Юли',
      'Август',
      'Септември',
      'Октомври',
      'Ноември',
      'Декември',
    ],
    weekdays: [
      'неделя',
      'понеделник',
      'вторник ',
      'сряда',
      'четвъртък',
      'петък',
      'събота',
    ],
    shortMonths: [
      'ян',
      'февр',
      'март',
      'април',
      'май',
      'юни',
      'юли',
      'авг',
      'септ',
      'окт',
      'ноем',
      'дек',
    ],
    decimalPoint: ',',
    downloadPNG: 'изтеглете като PNG',
    downloadJPEG: 'изтеглете като JPEG',
    downloadPDF: 'изтеглете като PDF',
    downloadSVG: 'изтеглете като SVG',
    exportButtonTitle: 'графичен експорт',
    loading: 'Зареждане, моля изчакайте...',
    printButtonTitle: 'отпечатайте графиката',
    resetZoom: 'нулиране',
    resetZoomTitle: 'нулиране',
    thousandsSep: ' ',
    viewFullscreen: 'цял екран',
    noData: 'Няма данни за',
  },
};

export const i18n = new VueI18n({
  locale: getInitialLanguage(),
  fallbackLocale: 'de',
  messages: { de, fr, en, it, bg },
});

window.a = i18n;
const initialState = {
  testimonials: window.__DATA__.testimonials,
  language: getInitialLanguage(),
  filter: getInitialFilter(),
  requestState: null,
  isBodyScrollPrevented: false,
  isLoginModalOpen: false,
  isRateModalOpen: false,
  isFilterOpen: getInitialIsFilterOpen(),
  isMenuDrawerOpen: false,
  isQuestionModalOpen: false,
  availableZipCodes: [],
  availableCantons: [],
};

export default new Vuex.Store({
  modules: {
    api: api.getStore(),
  },
  state: initialState,
  getters,
  mutations: {
    setIsMenuDrawerOpen: (state, value) => (state.isMenuDrawerOpen = value),
    setLanguage: (state, value) => (state.language = value),
    setRequestState: (state, value) => (state.requestState = value),
    setUser: (state, value) => (state.api.user = value),
    emptyPlaylists: (state) => (state.api.playlists = []),
    setFilter: (state, value) => (state.filter = value),
    setPlaylists: (state, value) => (state.playlists = value),
    resetApiUserErrors: (state) => (state.api.error.user = null),
    setIsBodyScrollPrevented: (state, value) =>
      (state.isBodyScrollPrevented = value),
    setIsLoginModalOpen: (state, value) => (state.isLoginModalOpen = value),
    setIsRateModalOpen: (state, value) => (state.isRateModalOpen = value),
    setIsFilterOpen: (state, value) => (state.isFilterOpen = value),
    setIsQuestionModalOpen: (state, value) =>
      (state.isQuestionModalOpen = value),
    setAvailableCantons: (state, value) => (state.availableCantons = value),
    setAvailableZipCodes: (state, value) => (state.availableZipCodes = value),
    setApiAskPhysio: (state, value) => (state.api.askPhysio = value),
  },
  actions: {
    toggleMenuDrawer: (context) =>
      context.commit('setIsMenuDrawerOpen', !context.state.isMenuDrawerOpen),
    setMenuDrawer: (context, value) =>
      context.commit('setIsMenuDrawerOpen', value),
    openLoginModal: (context) => {
      context.commit('setIsBodyScrollPrevented', true);
      context.commit('setIsLoginModalOpen', true);
    },
    closeLoginModal: (context) => {
      context.commit('setIsLoginModalOpen', false);
      context.commit('setIsBodyScrollPrevented', false);
      context.commit('resetApiUserErrors');
    },
    openRateModal: (context) => context.commit('setIsRateModalOpen', true),
    closeRateModal: (context) => context.commit('setIsRateModalOpen', false),
    openQuestionModal: (context) =>
      context.commit('setIsQuestionModalOpen', true),
    closeQuestionModal: (context) => {
      context.commit('setIsQuestionModalOpen', false);
      context.commit('setApiAskPhysio', null);
    },
    resetFilter: (context) => context.dispatch('setFilter', defaultFilterState),
    setFilter: (context, data) => {
      //context.commit('isFilterOpen', true);
      context.commit(
        'setFilter',
        [
          'favorites',
          'basfi',
          'duration',
          'resources',
          'area',
          'intensity',
          'dimensions',
          'portal',
        ].reduce((acc, prop) => {
          return {
            ...acc,
            [prop]:
              data[prop] === undefined
                ? context.state.filter[prop]
                : data[prop],
          };
        }, {})
      );

      window.localStorage.setItem(
        config.localStorageFilterField,
        JSON.stringify(context.state.filter)
      );
    },
    i18n_load_extra: async (context, data) => {
      const name = pathOr(data, 'name', data);
      const extension = pathOr('json', 'extension', data);

      return Promise.all(
        config.availableLanguages.map((language) => {
          try {
            return import(`@/locale/${language}/${name}.${extension}`).then(
              (res) => {
                i18n.setLocaleMessage(language, {
                  ...i18n.messages[language],
                  ...(extension === 'json' ? res : res.default),
                });
              }
            );
          } catch (error) {
            return null;
          }
        })
      );
    },
    setLanguage: async (context, language) => {
      i18n.locale = language;
      moment.locale(language);

      Highcharts.setOptions({
        lang: highchartsLang[language],
      });

      if (language !== 'en') {
        // en is loaded by default and can not be imported
        await import(`moment/locale/${language}`);
      }

      if (language in i18n.messages) {
      } else {
        try {
          const res = await import(`../locale/${language}/index.json`);
          i18n.setLocaleMessage(language, {
            ...i18n.messages[language],
            ...res,
          });
        } catch (error) {}
      }

      context.commit('setLanguage', language);
      window.localStorage.setItem(config.localStorageLanguageField, language);
    },
    favPlaylist: (context, { playlistId }) => {
      context.dispatch('api_updateUserFav', {
        data: qs.stringify({ playlist_id: playlistId }),
      });
    },
    ratePlaylist: (context, { feedback, stars, playlistId }) => {
      context.dispatch('api_ratePlaylist', {
        params: { playlistId },
        data: qs.stringify({ feedback, stars }),
      });
    },
    resetPassword: (context, { password, uid, token }) => {
      context
        .dispatch('api_resetPassword', {
          data: qs.stringify({ password, uid, token }),
        })
        .then(() => router.push({ path: '/' }));
    },
    requestResetPassword: (context, { email }) => {
      context.commit('setRequestState', 'loading');
      return A.post('/api/request_reset_password', qs.stringify({ email }))
        .then((r) => {
          if (r.data.status !== 'ok') throw new Error('Request Failed');
        })
        .then(() => context.commit('setRequestState', 'finished'));
    },
    saveBasfi: (context, { q0, q1, q2, q3, q4, q5, q6, q7, q8, q9 }) => {
      context.dispatch('addBasfi', {
        data: qs.stringify({ q0, q1, q2, q3, q4, q5, q6, q7, q8, q9 }),
      });
    },
    saveUser: (
      context,
      {
        firstName,
        lastName,
        sex,
        language,
        year,
        portal,
        canton,
        diagnosisYear,
        liveQualityLimitation,
        isUsingbefit,
      }
    ) => {
      context.dispatch('setLanguage', language);
      context.dispatch('setFilter', { portal });
      context.dispatch('updateUser', {
        data: {
          firstName,
          lastName,
          year,
          sex,
          language,
          portal,
          canton,
          diagnosisYear,
          liveQualityLimitation,
          isUsingbefit,
        },
      });
    },
    register: (
      context,
      {
        firstName,
        lastName,
        email,
        password,
        sex,
        agreement,
        portal,
        year,
        canton,
      }
    ) => {
      context
        .dispatch('signupUser', {
          data: qs.stringify({
            first_name: firstName,
            last_name: lastName,
            sex,
            email,
            year,
            canton,
            password,
            agreement,
            language: context.state.language,
            portal,
          }),
        })
        .then(() => router.push({ path: '/' }));
    },
    logout: (context) => {
      router.push({ path: '/' });
      return context.dispatch('logoutUser').then(() => {
        context.commit('emptyPlaylists');
        context.dispatch('getPlaylists');
        context.dispatch('resetFilter');
      });
    },
    login: ({ dispatch, getters }, { email, password }) => {
      return dispatch('loginUser', {
        data: qs.stringify({ email, password }),
      }).then(() => {
        dispatch('getPlaylists');
        dispatch('closeLoginModal');
        if (
          ['signup', 'reset-password', 'glad-signup'].includes(
            router.currentRoute.name
          )
        ) {
          router.push({ name: 'home' });
        }
      });
    },
    getPlaylist: ({ dispatch, getters }, id) => {
      const playlist = find({ id }, getters.playlists);
      if (!playlist || !playlist.hasDetails) {
        return dispatch('api_getPlaylist', { params: { id } });
      }
    },
    getProgramms: ({ dispatch }) => {
      dispatch('api_getProgramms');
    },
    getProgramm: ({ dispatch, getters }, id) => {
      const playlist = find({ id }, getters.playlists);
      if (!playlist || !playlist.hasDetails) {
        return dispatch('api_getProgramm', { params: { id } });
      }
    },
    askPhysio: ({ dispatch }, { question, name, email }) => {
      return dispatch('api_askPhysio', {
        data: qs.stringify({ question, name, email }),
      });
    },
    loadAvailableZipCodes: ({ state, commit }) => {
      if (state.availableZipCodes.length < 1) {
        import('@/../../data/zipCodes.json').then((zipCodes) =>
          commit('setAvailableZipCodes', zipCodes)
        );
      }
    },
    loadAvailableCantons: ({ state, commit }) => {
      if (state.availableCantons.length < 1) {
        import('@/../../data/cantons.json').then((cantons) =>
          commit('setAvailableCantons', cantons)
        );
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/store/index.js